import { useState } from 'react';
import { ChevronDown, ChevronRight, Lock, LogOut } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';
import { logout } from '../../services/auth.service';
import UserStatusCard from './UserStatusCard';
import { useCurrentUser } from '@beholos/holoscommon';
// BASE_URL not required here

type FooterLink = {
  href: string;
  label: string;
  anchor?: boolean;
  external?: boolean;
};


export default function SideBar() {
  const { user } = useCurrentUser();
  const [homeExpanded, setHomeExpanded] = useState(false);
  const [experiencesExpanded, setExperiencesExpanded] = useState(false);
  const [sessionsExpanded, setSessionsExpanded] = useState(false);
  
  const linkBase = 'text-base text-gray-500 block px-4 py-1 hover:text-black border-l border-gray-400';

  const primaryLinks = [
    { href: '/mi-cuenta', label: 'Mi perfil' },
    // { href: '/mi-historial', label: 'Mi historial de bienestar' },  
    { href: '/mi-membresia', label: 'Mi plan', isBeta: true },
    { href: '/plan-familiar', label: 'Plan Familiar', isBeta: true },
    { href: 'https://www.beholos.com/para-empresas/referidos', label: 'Refiere a tu empresa' },
    { href: '/mis-logros', label: 'Mis logros' },
    { href: '/mis-clases', label: 'Mis clases' },
    { href: '/mis-programas', label: 'Mis programas y retos' },
  ];

  const experienceLinks = [
    {
      label: 'Videos on-demand',
      href: '/clases-online',
      locked: !user?.hasPlan,
    },
    {
      label: 'Programas y retos',
      href: '/programas',
      locked: false,
    },
    {
      label: 'Clases',
      href: '/clases',
      locked: !user?.hasPlan,
    },
  ];

  const sessionLinks = [
    {
      label: 'Nutrición',
      href: '/nutricion',
      locked:
        !user?.hasPlan /* ||
        user?.partnerTypeRestriction.includes('nutritionist'), */
    },
    {
      label: 'Health coach',
      href: '/health-coach',
      locked:
        !user?.hasPlan /* ||
        user?.partnerTypeRestriction.includes('healthCoach'), */
    },
    {
      label: 'Finanzas',
      href: '/finanzas',
      locked:
        !user?.hasPlan /* ||
        user?.partnerTypeRestriction.includes('financialAdvisor'), */
    },
    {
      label: 'Psicología',
      href: '/psicologia',
      locked:
        !user?.hasPlan /* ||
        user?.partnerTypeRestriction.includes('psychologist'), */
    },
    {
      label: 'Personal training',
      href: '/personal-training',
      locked:
        !user?.hasPlan /* ||
        user?.partnerTypeRestriction.includes('fitnessCoach'), */
    },
  ];

  const footerLinks: FooterLink[] = [
    {
      href: '/partners',
      label: 'Partners',
    },
    {
      href: '/preguntas-frecuentes',
      label: 'Preguntas frecuentes',
      anchor: true,
    },
  ];

  const lockedIcon = (
    <div className="absolute top-1 right-2 text-gray-400">
      <Lock />
    </div>
  );

  return (
    <aside className="hidden md:flex flex-col w-full bg-white h-full border-r border-gray-400">
      <div className="flex-grow overflow-y-auto px-12 py-8">
        <UserStatusCard />

        <div
          id="user-rewards-points"
          data-firstname={user?.firstname}
          data-has-plan={user?.hasPlan ? 'true' : 'false'}
        />

        <div className="my-2">
          <button
            onClick={() => setHomeExpanded(!homeExpanded)}
            className="flex items-center w-full my-3 text-base font-medium text-black cursor-pointer"
          >
            {homeExpanded ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
            <NavLink
              to={'/'}
              className={({ isActive }) => (
                isActive ? 'text-holos-dark' : 'text-black'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              Inicio
            </NavLink>
          </button>
          {homeExpanded && primaryLinks
            .filter(link => link.href !== '/plan-familiar' || user?.isCorporate)
            .map((link) => (
            <NavLink
              key={link.label}
              to={link.href}
              className={({ isActive }) => (
                isActive ?
                  'text-base block px-4 py-1 hover:text-holos-dark text-holos-dark font-medium border-l border-gray-400' :
                  'text-base text-gray-500 block px-4 py-1 hover:text-black border-l border-gray-400'
              )}
            >
              {link.label}
              {link.isBeta && (
                <span className="ml-2 inline-block bg-prosper/70 text-black text-xs font-semibold px-2 py-0.5 rounded-full border border-black">
                  beta
                </span>
              )}
            </NavLink>
          ))}
        </div>

        <button
          onClick={() => setExperiencesExpanded(!experiencesExpanded)}
          className="flex items-center w-full my-3 text-base font-medium text-black cursor-pointer"
        >
          {experiencesExpanded ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
          Experiencias
        </button>
        {experiencesExpanded && experienceLinks.map((link) => (
          <div key={link.label} className="relative">
            {link.locked ? (
              <>
                <span className={linkBase}>{link.label}</span>
                {lockedIcon}
              </>
            ) : (
              <NavLink
                to={link.href}
                className={({ isActive }) => (
                  isActive ?
                    'text-base block px-4 py-1 hover:text-holos-dark text-holos-dark font-medium border-l border-gray-400' :
                    'text-base text-gray-500 block px-4 py-1 hover:text-black border-l border-gray-400'
                )}>
                {link.label}
              </NavLink>
            )}
          </div>
        ))}

        <button
          onClick={() => setSessionsExpanded(!sessionsExpanded)}
          className="flex items-center w-full my-3 text-base font-medium text-black cursor-pointer"
        >
          {sessionsExpanded ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
          Agendar una sesión
        </button>
        {sessionsExpanded && sessionLinks.map((link) => (
          <div key={link.label} className="relative">
            {link.locked ? (
              <>
                <span className={linkBase}>{link.label}</span>
                {lockedIcon}
              </>
            ) : (
              <NavLink
                to={link.href}
                className={({ isActive }) => (
                  isActive ?
                    'text-base block px-4 py-1 hover:text-holos-dark text-holos-dark font-medium border-l border-gray-400' :
                    'text-base text-gray-500 block px-4 py-1 hover:text-black border-l border-gray-400'
                )}>
                {link.label}
              </NavLink>
            )}
          </div>
        ))}

        <div className="my-2">
          {footerLinks.map((link) => (
            link.anchor ? (
              <a
                key={link.label}
                href={link.href}
                className="text-base font-medium text-black block my-1"
              >
                {link.label}
              </a>
            ) : link.external ? (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-medium text-black block my-1"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="text-base font-medium text-black block my-1"
              >
                {link.label}
              </Link>
            )
          ))}
        </div>
      </div>

      <div className="px-4 py-2 border-t border-gray-400">
        <button onClick={logout} className="flex items-center text-base font-semibold text-black cursor-pointer">
          <LogOut className='mr-2' />
          Cerrar sesión
        </button>
      </div>
    </aside>
  );
}

// Mobile-friendly sidebar content to reuse inside drawers/overlays
// Note: Logic and structure mirrors the default sidebar. Comments preserved.
export function SidebarContent({ user, onNavigate }: { user: any; onNavigate?: () => void }) {
  const [homeExpanded, setHomeExpanded] = useState(false);
  const [experiencesExpanded, setExperiencesExpanded] = useState(false);
  const [sessionsExpanded, setSessionsExpanded] = useState(false);
  const linkBase = 'text-base text-gray-500 block px-4 py-1 hover:text-black border-l border-gray-400';

  const primaryLinks = [
    { href: '/mi-cuenta', label: 'Mi perfil' },
    // { href: '/mi-historial', label: 'Mi historial de bienestar' },  
    { href: '/mi-membresia', label: 'Mi plan', isBeta: true },
    { href: '/plan-familiar', label: 'Plan Familiar', isBeta: true },
    { href: 'https://www.beholos.com/para-empresas/referidos', label: 'Refiere a tu empresa' },
    { href: '/mis-logros', label: 'Mis logros' },
    { href: '/mis-clases', label: 'Mis clases' },
    { href: '/mis-programas', label: 'Mis programas y retos' },
  ];

  const experienceLinks = [
    {
      label: 'Videos on-demand',
      href: '/clases-online',
      locked: !user?.hasPlan,
    },
    {
      label: 'Programas y retos',
      href: '/programas',
      locked: false,
    },
    {
      label: 'Clases',
      href: '/clases',
      locked: !user?.hasPlan,
    },
  ];

  const sessionLinks = [
    {
      label: 'Nutrición',
      href: '/nutricion',
      locked:
        !user?.hasPlan /* ||
        user?.partnerTypeRestriction.includes('nutritionist'), */
    },
    {
      label: 'Health coach',
      href: '/health-coach',
      locked:
        !user?.hasPlan /* ||
        user?.partnerTypeRestriction.includes('healthCoach'), */
    },
    {
      label: 'Finanzas',
      href: '/finanzas',
      locked:
        !user?.hasPlan /* ||
        user?.partnerTypeRestriction.includes('financialAdvisor'), */
    },
    {
      label: 'Psicología',
      href: '/psicologia',
      locked:
        !user?.hasPlan /* ||
        user?.partnerTypeRestriction.includes('psychologist'), */
    },
    {
      label: 'Personal training',
      href: '/personal-training',
      locked:
        !user?.hasPlan /* ||
        user?.partnerTypeRestriction.includes('fitnessCoach'), */
    },
  ];

  const footerLinks: FooterLink[] = [
    {
      href: '/partners',
      label: 'Partners',
    },
    {
      href: '/preguntas-frecuentes',
      label: 'Preguntas frecuentes',
      anchor: true,
    },
  ];

  const lockedIcon = (
    <div className="absolute top-1 right-2 text-gray-400">
      <Lock />
    </div>
  );

  return (
    <div className="flex flex-col w-full bg-white h-full">
      <div className="flex-grow overflow-y-auto px-12 py-8">
        <UserStatusCard />

        <div
          id="user-rewards-points"
          data-firstname={user?.firstname}
          data-has-plan={user?.hasPlan ? 'true' : 'false'}
        />

        <div className="my-2">
          <button
            onClick={() => setHomeExpanded(!homeExpanded)}
            className="flex items-center w-full my-3 text-base font-medium text-black cursor-pointer"
          >
            {homeExpanded ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
            <NavLink
              to={'/'}
              onClick={onNavigate}
              className={({ isActive }) => (
                isActive ? 'text-holos-dark' : 'text-black'
              )}
            >
              Inicio
            </NavLink>
          </button>
          {homeExpanded && primaryLinks
            .filter(link => link.href !== '/plan-familiar' || user?.isCorporate)
            .map((link) => (
            <NavLink
              key={link.label}
              to={link.href}
              onClick={onNavigate}
              className={({ isActive }) => (
                isActive ?
                  'text-base block px-4 py-1 hover:text-holos-dark text-holos-dark font-medium border-l border-gray-400' :
                  'text-base text-gray-500 block px-4 py-1 hover:text-black border-l border-gray-400'
              )}
            >
              {link.label}
              {link.isBeta && (
                <span className="ml-2 inline-block bg-prosper/70 text-black text-xs font-semibold px-2 py-0.5 rounded-full border border-black">
                  beta
                </span>
              )}
            </NavLink>
          ))}
        </div>

        <button
          onClick={() => setExperiencesExpanded(!experiencesExpanded)}
          className="flex items-center w-full my-3 text-base font-medium text-black cursor-pointer"
        >
          {experiencesExpanded ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
          Experiencias
        </button>
        {experiencesExpanded && experienceLinks.map((link) => (
          <div key={link.label} className="relative">
            {link.locked ? (
              <>
                <span className={linkBase}>{link.label}</span>
                {lockedIcon}
              </>
            ) : (
              <NavLink
                to={link.href}
                onClick={onNavigate}
                className={({ isActive }) => (
                  isActive ?
                    'text-base block px-4 py-1 hover:text-holos-dark text-holos-dark font-medium border-l border-gray-400' :
                    'text-base text-gray-500 block px-4 py-1 hover:text-black border-l border-gray-400'
                )}>
                {link.label}
              </NavLink>
            )}
          </div>
        ))}

        <button
          onClick={() => setSessionsExpanded(!sessionsExpanded)}
          className="flex items-center w-full my-3 text-base font-medium text-black cursor-pointer"
        >
          {sessionsExpanded ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
          Agendar una sesión
        </button>
        {sessionsExpanded && sessionLinks.map((link) => (
          <div key={link.label} className="relative">
            {link.locked ? (
              <>
                <span className={linkBase}>{link.label}</span>
                {lockedIcon}
              </>
            ) : (
              <NavLink
                to={link.href}
                onClick={onNavigate}
                className={({ isActive }) => (
                  isActive ?
                    'text-base block px-4 py-1 hover:text-holos-dark text-holos-dark font-medium border-l border-gray-400' :
                    'text-base text-gray-500 block px-4 py-1 hover:text-black border-l border-gray-400'
                )}>
                {link.label}
              </NavLink>
            )}
          </div>
        ))}

        <div className="my-2">
          {footerLinks.map((link) => (
            link.anchor ? (
              <a
                key={link.label}
                href={link.href}
                className="text-base font-medium text-black block my-1"
                onClick={onNavigate}
              >
                {link.label}
              </a>
            ) : link.external ? (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-medium text-black block my-1"
                onClick={onNavigate}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="text-base font-medium text-black block my-1"
                onClick={onNavigate}
              >
                {link.label}
              </Link>
            )
          ))}
        </div>
      </div>

      <div className="px-4 py-2 border-t border-gray-400">
        <button onClick={logout} className="flex items-center text-base font-semibold text-black cursor-pointer">
          <LogOut className='mr-2' />
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
